import { motion } from "framer-motion"
import { formatDate } from "lopermut"
import { InfoIcon, RadarIcon } from "lucide-react"
import { ReactNode, useEffect, useMemo, useRef } from "react"
import { Bar, BarChart, CartesianGrid, Cell, TooltipContentProps, XAxis, YAxis } from "recharts"
import { Tooltip, TooltipContent, TooltipTrigger } from "../../atoms/Tooltip"
import { getChartPalette, getFillColor } from "../../lib/chart-utils"
import { CardContent } from "../../molecules/Card"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../molecules/Chart"
import { cn } from "../../utils"

const ACTIVITY_BAR_SIZE = 20

type StackedDatum = {
  date?: string | null
  top1?: number
  top2?: number
  top3?: number
  top4?: number
  top5?: number
  others?: number
  opacity?: number
}

const safeFormat = (date: string) => {
  try {
    return formatDate(date, "MMM d")
  } catch (error) {
    return null
  }
}

type Props = {
  data?: StackedDatum[]
  isPending: boolean
  chartConfig: ChartConfig
  tooltipInfo?: string
  noDataT?: {
    title: string
    description: string
  }
  action?: ReactNode
  chartClassName?: string
  title?: string
  description?: string
  colorPalette?: "primary" | "secondary"
  hideZeroTooltipEntries?: boolean
}

export const StackedActivityChart = ({
  data,
  isPending,
  chartConfig,
  tooltipInfo,
  noDataT,
  action,
  chartClassName,
  title,
  description,
  colorPalette = "primary",
  hideZeroTooltipEntries = false,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const palette = useMemo(() => {
    const paletteType = colorPalette === "primary" ? "primary" : "support"
    return getChartPalette(paletteType, "stack", 5)
  }, [colorPalette])

  const resolveSeriesFill = (seriesKey: string, paletteIndex: number, fallbackVar: string) => {
    const configured = chartConfig[seriesKey]?.color
    if (configured) return configured
    return getFillColor(palette[paletteIndex], fallbackVar)
  }

  const computedData = useMemo(() => {
    if (!data || data.length === 0) return []

    return data.map((item) => {
      const data: StackedDatum = {
        date: item.date ? safeFormat(item.date) : null,
        top1: item.top1 ?? 0,
        top2: item.top2 ?? 0,
        top3: item.top3 ?? 0,
        top4: item.top4 ?? 0,
        top5: item.top5 ?? 0,
        others: item.others ?? 0,
        opacity: item.opacity ?? 1,
      }

      return data
    })
  }, [data])

  const noData = useMemo(() => {
    if (isPending) return false
    if (!computedData.length) return true
    const total = computedData.reduce(
      (sum, d) =>
        sum +
        (d.top1 || 0) +
        (d.top2 || 0) +
        (d.top3 || 0) +
        (d.top4 || 0) +
        (d.top5 || 0) +
        (d.others || 0),
      0,
    )
    return total === 0
  }, [computedData, isPending])

  const fallbackData = useMemo(() => {
    if (!noData) return []
    const days = 30
    return Array.from({ length: days }, (_, i) => {
      const date = new Date()
      date.setDate(date.getDate() - (days - 1 - i))
      const formattedDate = formatDate(date, "MMM d")

      return {
        date: formattedDate,
        top1: 50 + ((i + Math.pow(-1, i)) % 2),
        top2: 30 + ((i + Math.pow(-10, i)) % 3),
        top3: 20 + ((i + Math.pow(-10, i)) % 4),
        top4: 10 + ((i + Math.pow(-10, i)) % 5),
        top5: 5 + ((i + Math.pow(-10, i)) % 6),
        others: 30 + ((i + Math.pow(-10, i)) % 6),
        opacity: 1,
      }
    })
  }, [noData])

  const chartData = noData ? fallbackData : computedData
  const renderOpacityCells = () =>
    chartData.map((item, index) => <Cell key={index} fillOpacity={item.opacity ?? 1} />)

  useEffect(() => {
    if (containerRef.current && chartData?.length) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth
    }
  }, [chartData])

  return (
    <div className="flex flex-col w-full relative rounded-none border-none gap-2">
      <div
        className={cn(
          "flex flex-row justify-between gap-4 pr-4",
          !action && !title && "justify-end",
        )}
      >
        {title && (
          <div className="flex flex-col gap-2 w-full max-w-md pb-4">
            <h2 className="text-md text-foreground font-medium">{title}</h2>
            {description && (
              <p className="text-sm text-muted-foreground text-stroke-sm">{description}</p>
            )}
          </div>
        )}

        {action}
        <LegendFromConfig
          className={cn(noData && "pointer-events-none grayscale opacity-15")}
          chartConfig={chartConfig}
          tooltipInfo={tooltipInfo}
          colorPalette={colorPalette}
        />
      </div>
      {noData && (
        <div className="flex items-center justify-center absolute inset-0 z-10 p-4">
          <div className="flex flex-col gap-2 py-14 items-center justify-center text-center m-auto max-w-sm">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <RadarIcon className="size-10 text-muted-foreground mb-3" strokeWidth={1.5} />
            </motion.div>
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <h2 className="text-md text-foreground text-stroke">{noDataT?.title}</h2>
            </motion.div>
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <p className="text-sm text-muted-foreground text-stroke-sm">{noDataT?.description}</p>
            </motion.div>
          </div>
        </div>
      )}
      <CardContent
        className={cn(
          "flex flex-col p-0 gap-2 items-end",
          noData && "pointer-events-none grayscale opacity-15",
        )}
      >
        <div ref={containerRef} className="w-full overflow-x-auto pb-2 -mb-2">
          <ChartContainer
            config={chartConfig}
            className={cn("aspect-auto h-[180px]", chartClassName)}
            style={{
              width: `${(chartData?.length ?? 0) * ACTIVITY_BAR_SIZE}px`,
              minWidth: "100%",
            }}
          >
            <BarChart accessibilityLayer data={chartData} margin={{ bottom: 0, top: 5 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={30}
              />
              <YAxis tickLine={false} axisLine={false} tickMargin={10} width={30} hide={true} />
              <ChartTooltip
                content={(props: TooltipContentProps) => {
                  const payload = hideZeroTooltipEntries
                    ? props.payload?.filter((item) => {
                        const value =
                          typeof item.value === "number" ? item.value : Number(item.value)
                        return Number.isFinite(value) && value !== 0
                      })
                    : props.payload

                  return <ChartTooltipContent {...props} payload={payload} />
                }}
              />
              {/* Order matters for stacking: others at bottom, then top5..top1 */}
              {"others" in (chartData[0] || {}) && (
                <Bar
                  name={"others"}
                  dataKey="others"
                  stackId="a"
                  fill={`var(--neutral)`}
                  barSize={ACTIVITY_BAR_SIZE}
                >
                  {renderOpacityCells()}
                </Bar>
              )}
              {"top5" in (chartData[0] || {}) && (
                <Bar
                  name={"top5"}
                  dataKey="top5"
                  stackId="a"
                  fill={resolveSeriesFill("top5", 4, "var(--chart-5)")}
                  barSize={ACTIVITY_BAR_SIZE}
                >
                  {renderOpacityCells()}
                </Bar>
              )}
              {"top4" in (chartData[0] || {}) && (
                <Bar
                  name={"top4"}
                  dataKey="top4"
                  stackId="a"
                  fill={resolveSeriesFill("top4", 3, "var(--chart-4)")}
                  barSize={ACTIVITY_BAR_SIZE}
                >
                  {renderOpacityCells()}
                </Bar>
              )}
              {"top3" in (chartData[0] || {}) && (
                <Bar
                  name={"top3"}
                  dataKey="top3"
                  stackId="a"
                  fill={resolveSeriesFill("top3", 2, "var(--chart-3)")}
                  barSize={ACTIVITY_BAR_SIZE}
                >
                  {renderOpacityCells()}
                </Bar>
              )}
              {"top2" in (chartData[0] || {}) && (
                <Bar
                  name={"top2"}
                  dataKey="top2"
                  stackId="a"
                  fill={resolveSeriesFill("top2", 1, "var(--chart-2)")}
                  barSize={ACTIVITY_BAR_SIZE}
                >
                  {renderOpacityCells()}
                </Bar>
              )}
              {"top1" in (chartData[0] || {}) && (
                <Bar
                  name={"top1"}
                  dataKey="top1"
                  stackId="a"
                  fill={resolveSeriesFill("top1", 0, "var(--chart-1)")}
                  barSize={ACTIVITY_BAR_SIZE}
                >
                  {renderOpacityCells()}
                </Bar>
              )}
            </BarChart>
          </ChartContainer>
        </div>
      </CardContent>
    </div>
  )
}

const LegendFromConfig = ({
  className,
  chartConfig,
  tooltipInfo,
  colorPalette = "primary",
}: {
  className?: string
  chartConfig: ChartConfig
  tooltipInfo?: string
  colorPalette?: "primary" | "secondary"
}) => {
  const palette = getChartPalette(colorPalette === "primary" ? "primary" : "support", "stack", 5)
  const items: Array<{ key: string; label: string; colorVar: string } | undefined> = [
    chartConfig["top1"] && {
      key: "top1",
      label: (chartConfig["top1"]?.label as string) || "Top 1",
      colorVar:
        (chartConfig["top1"]?.color as string | undefined) ??
        `var(--${palette[0]?.color ?? "chart-1"})`,
    },
    chartConfig["top2"] && {
      key: "top2",
      label: (chartConfig["top2"]?.label as string) || "Top 2",
      colorVar:
        (chartConfig["top2"]?.color as string | undefined) ??
        `var(--${palette[1]?.color ?? "chart-2"})`,
    },
    chartConfig["top3"] && {
      key: "top3",
      label: (chartConfig["top3"]?.label as string) || "Top 3",
      colorVar:
        (chartConfig["top3"]?.color as string | undefined) ??
        `var(--${palette[2]?.color ?? "chart-3"})`,
    },
    chartConfig["top4"] && {
      key: "top4",
      label: (chartConfig["top4"]?.label as string) || "Top 4",
      colorVar:
        (chartConfig["top4"]?.color as string | undefined) ??
        `var(--${palette[3]?.color ?? "chart-4"})`,
    },
    chartConfig["top5"] && {
      key: "top5",
      label: (chartConfig["top5"]?.label as string) || "Top 5",
      colorVar:
        (chartConfig["top5"]?.color as string | undefined) ??
        `var(--${palette[4]?.color ?? "chart-5"})`,
    },
    chartConfig["others"] && {
      key: "others",
      label: (chartConfig["others"]?.label as string) || "Others",
      colorVar: "var(--neutral)",
    },
  ].filter(Boolean)

  return (
    <div className={cn("flex items-center gap-4 flex-wrap justify-end", className)}>
      {items.map(
        (el) =>
          el && (
            <div key={el.key} className="flex items-center gap-1.5">
              <div className="size-2 rounded-full" style={{ background: el.colorVar }} />
              <span className="text-sm text-muted-foreground">{el.label}</span>
            </div>
          ),
      )}
      {tooltipInfo && (
        <Tooltip>
          <TooltipTrigger>
            <InfoIcon className="size-4 text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltipInfo}</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}
